import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { setIsLoginModalOpen } from "../../../store/reducers/global-reducer";
import { toast } from "react-toastify";
import axios from "axios";
import PlaceOrderModal from "./services/PlaceOrderModal";
import ProductMilestoneModal from "../../../containers/user/productCheckout/ProductMilestoneModal";
import {clearCart} from "../../../store/reducers/cart-slice-reducer"
import { resolveCategoryId } from "../../../utils/resolveCategoryId";
import { resolveBuyerTaxPreviewError } from "../../../utils/buyerTaxPreview";

function Total({ couponData, sellerId }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);

  
  // Add null checks for localStorage
  const authInfo = (() => {
    try {
      return JSON.parse(localStorage.getItem("authInfo")) || null;
    } catch {
      return null;
    }
  })();
  
  const userInfo = (() => {
    try {
      return JSON.parse(localStorage.getItem("userInfo")) || null;
    } catch {
      return null;
    }
  })();

  // Safe Redux selector with multiple fallbacks
  const cart = useSelector((state) => {
    try {
      return state?.cart?.cart || [];
    } catch {
      return [];
    }
  });

  const [total, setTotal] = useState({
    totalPrice: 0,
    totalQuantity: 0,
    actualPrice: 0,
    dealDiscountAmount: 0,
    checkoutLines: [],
  });
  const [taxPreview, setTaxPreview] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxPreviewError, setTaxPreviewError] = useState(null);
  const [userDataAvailable, setUserDataAvailable] = useState(false);
  const [showOrderPopup, setShowOrderPopup] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);
  
  const handleOpenMilestoneModal = () => setShowMilestoneModal(true);
  const handleCloseMilestoneModal = () => setShowMilestoneModal(false);

  useEffect(() => {
    if (authInfo) {
      getUserData();
    }
  }, [])

  useEffect(() => {
    const calculateTotal = async () => {
      // Reset totals if cart is empty or invalid
      if (!Array.isArray(cart) || cart.length === 0) {
        setTotal({
          totalPrice: 0,
          totalQuantity: 0,
          actualPrice: 0,
          dealDiscountAmount: 0,
          checkoutLines: [],
        });
        return;
      }

      let totalQuantity = 0;
      let totalPrice = 0;
      let actualPrice = 0;
      let dealDiscountAmount = 0;
      const checkoutLines = [];

      for (const item of cart) {
        // Skip invalid items
        if (!item || typeof item.price !== 'number' || typeof item.quantity !== 'number') {
          console.warn('Skipping invalid cart item:', item);
          continue;
        }

        let finalPrice = item.price;
        if (item.discountId) {
          try {
            const response = await axios.get(`front/discount-status/${item.discountId}`);
            const data = response?.data?.data;
            if (data?.isActive) {
              finalPrice = item.price - (item.price * data.discount) / 100;
              dealDiscountAmount += (item.price - finalPrice) * item.quantity;
            }
          } catch (error) {
            console.error("Error fetching discount status:", error);
            // Continue with original price if discount fetch fails
          }
        }

        totalQuantity += item.quantity;
        totalPrice += finalPrice * item.quantity;
        actualPrice += item.price * item.quantity;
        checkoutLines.push({
          id: item.id,
          name: item.name,
          price: finalPrice,
          quantity: item.quantity,
          categoryId: resolveCategoryId(item),
        });
      }

      setTotal({
        totalPrice: totalPrice.toFixed(2),
        totalQuantity,
        actualPrice: actualPrice.toFixed(2),
        dealDiscountAmount: dealDiscountAmount.toFixed(2),
        checkoutLines,
      });
    };

    calculateTotal();
  }, [cart, couponData]);

  const amount = couponData !== null
    ? (total.totalPrice - total.totalPrice * couponData.discount_per / 100).toFixed(2)
    : total.totalPrice;

  const couponFactor = couponData ? 1 - Number(couponData.discount_per || 0) / 100 : 1;

  useEffect(() => {
    const loadTaxPreview = async () => {
      if (!authInfo?.token || !cart?.length || parseFloat(amount) <= 0) {
        setTaxPreview(null);
        setTaxPreviewError(
          !authInfo?.token
            ? "Log in as a buyer to see tax and platform fee."
            : null
        );
        return;
      }
      setTaxLoading(true);
      setTaxPreviewError(null);
      try {
        const linesForPreview =
          total.checkoutLines?.length > 0
            ? total.checkoutLines.map((line) => ({
                ...line,
                price: Number((line.price * couponFactor).toFixed(2)),
                categoryId: resolveCategoryId(line),
              }))
            : cart.map((item) => ({
                id: item.id,
                name: item.name,
                price: Number((item.price * couponFactor).toFixed(2)),
                quantity: item.quantity,
                categoryId: resolveCategoryId(item),
              }));

        const response = await axios.post(
          "user/checkout/preview-tax",
          {
            cart: linesForPreview,
            sellerId: sellerId || cart[0]?.createdBy || cart[0]?.sellerId,
          },
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json;charset=UTF-8",
              Authorization: `Bearer ${authInfo.token}`,
            },
          }
        );
        if (response.data?.status && response.data?.data) {
          setTaxPreview(response.data.data);
          const taxErr = response.data.data.taxError;
          setTaxPreviewError(
            resolveBuyerTaxPreviewError(taxErr?.code, taxErr?.message)
          );
        } else {
          setTaxPreview(null);
          setTaxPreviewError(
            resolveBuyerTaxPreviewError(response.data?.code, response.data?.message)
          );
        }
      } catch (err) {
        console.error("Tax preview failed:", err);
        setTaxPreview(null);
        setTaxPreviewError(
          resolveBuyerTaxPreviewError(
            err.response?.data?.code,
            err.response?.data?.message
          )
        );
      } finally {
        setTaxLoading(false);
      }
    };
    loadTaxPreview();
  }, [cart, amount, sellerId, authInfo?.token, total.checkoutLines, couponFactor]);

  const itemsSubtotalDisplay = Number(amount).toFixed(2);
  const platformFeeDisplay =
    taxPreview?.platformFee != null
      ? Number(taxPreview.platformFee).toFixed(2)
      : null;
  const taxAmountDisplay = taxPreview?.preview?.taxAmount != null
    ? Number(taxPreview.preview.taxAmount).toFixed(2)
    : null;
  const taxLabel = taxPreview?.taxLabel || "Tax";
  const taxIncludedInFinal =
    taxAmountDisplay != null && Number(taxAmountDisplay) > 0;
  const finalAmount = taxPreview?.preview?.totalInclusive != null
    ? Number(taxPreview.preview.totalInclusive).toFixed(2)
    : platformFeeDisplay != null
      ? (Number(amount) + Number(platformFeeDisplay)).toFixed(2)
      : amount;
  const additionalData = {
    finalAmount,
    discount: couponData ? (total.totalPrice * couponData.discount_per / 100).toFixed(2) : "0",
    deliveryCharge: "0",
    taxAmount: taxAmountDisplay ?? "0",
    platformFee: platformFeeDisplay ?? "0",
  };

  // Check if we should show milestone button or auto-create milestone
  const shouldShowMilestoneButton = () => {
    // Show button only if there's more than 1 product OR if single product has quantity > 1
    if (!cart || cart.length === 0) return false;
    return cart.length > 1 || (cart.length === 1 && cart[0].quantity > 1);
  };

  // Auto-create single milestone for single product with quantity 1
  const createSingleMilestone = () => {
    if (!authInfo) {
      toast.error("Login required to create milestone");
      dispatch(setIsLoginModalOpen({ isLoginModalOpen: true }));
      document.body.style.overflow = "hidden";
      return;
    }

    if (!cart || cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    // For single item with quantity 1, proceed directly to checkout
    // The milestone logic will be handled in the checkout process
    toast.info("Creating milestone order...");
    handleCheckout(); // Use existing checkout flow
  };

  const handleCheckout = async () => {
    if (!cart || cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    if (!userInfo) {
      toast.error("User info not available");
      return;
    }

    const safeAdditionalData = additionalData || {
      finalAmount: finalAmount || 0,
      discount: 0,
      deliveryCharge: 0,
      taxAmount: 0,
    };

    const reqBody = {
      amount: parseFloat(finalAmount) || 0,
      quantity: total?.totalQuantity || 0,
      name: userInfo.name,
      email: userInfo.email,
      cart: cart.map((item) => ({
        ...item,
        sellerId: sellerId || item.createdBy,
        createdBy: sellerId || item.createdBy,
      })),
      sellerId: sellerId,
      additionalData: safeAdditionalData,
    };

    console.log("🔍 Checkout request with enhanced cart:", reqBody);

    try {
      const response = await axios.post("user/createPaymentIntent/", reqBody, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${authInfo.token}`,
        },
      });

      console.log("Payment intent response:", response.data);

      if (
        response.data.status &&
        response.data.data &&
        response.data.data.clientSecret
      ) {
        const piData = response.data.data;
        const previewTotal =
          piData.preview?.totalInclusive != null
            ? Number(piData.preview.totalInclusive)
            : Number(finalAmount);
        history.push({
          pathname: "/orderCheckout",
          state: {
            client_secret: piData.clientSecret,
            amount: Math.round(previewTotal * 100),
            pricing: {
              subtotal: itemsSubtotalDisplay,
              platformFee: piData.platformFee ?? platformFeeDisplay ?? "0",
              taxAmount: piData.preview?.taxAmount ?? taxAmountDisplay ?? "0",
              taxLabel: taxPreview?.taxLabel || "Tax",
              total: previewTotal.toFixed(2),
            },
            metadata: {
              cart: JSON.stringify(cart),
            },
          },
        });
      } else {
        const errorMessage =
          response.data.error ||
          response.data.message ||
          "Failed to initialize payment";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Checkout error:", error);

      if (error.response?.data) {
        toast.error(
          error.response.data.error ||
            error.response.data.message ||
            "Failed to initialize payment"
        );
      } else if (error.request) {
        toast.error("Network error. Please check your connection and try again.");
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    }
  };

  const getUserData = () => {
    const userId = authInfo.id
    axios.get('user/my-profile/' + userId, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json;charset=UTF-8',
        'Authorization': `Bearer ${authInfo.token}`
      }
    }).then((response) => {
      if (response.data.status) {
        let resData = response.data.data;

        const isValid = resData?.name &&
          resData?.email &&
          resData?.phone &&
          resData?.address?.street &&
          resData?.address?.city &&
          resData?.address?.state &&
          resData?.address?.country &&
          resData?.address?.zip;

        if (isValid) {
          setUserDataAvailable(true)
        } else {
          setUserDataAvailable(false)
          console.log("Some fields are missing. User data set to false.");
        }
      }
    }).catch(error => {
      console.log(error)
    }).finally(() => {
      setTimeout(() => {
        // dispatch(setLoading({ loading: false }));
      }, 300);
    });
  }

  const openmodalHandler = async () => {
  setIsLoading(true);
  try {
    if (!authInfo) {
      toast.error("Buyer login failed...");
      dispatch(setIsLoginModalOpen({ isLoginModalOpen: true }));
      document.body.style.overflow = "hidden";
    } else {
      await handleCheckout(); 
    }
  } finally {
    setIsLoading(false);
  }
};


  // Handler for milestone button click
  const handleMilestoneClick = () => {
    if (!authInfo) {
      toast.error("Login required to create milestones");
      dispatch(setIsLoginModalOpen({ isLoginModalOpen: true }));
      document.body.style.overflow = "hidden";
    } else if (!cart || cart.length === 0) {
      toast.error("Cart is empty");
    } else if (shouldShowMilestoneButton()) {
      // Show modal for multiple products or multiple quantities
      handleOpenMilestoneModal();
    } else {
      // Auto-create single milestone
      createSingleMilestone();
    }
  };

  return (
    <div>
      <div className="cart_footer cart_wrap border-bottom">
        <div className="cart_foot_price">
          <div className="cfp">
            <span>Total Item </span>
            <b>{total.totalQuantity}<small>(in Qty)</small></b>
          </div>
          <div className="cfp">
            <span>Total Price </span>
            <b>${total.actualPrice}</b>
          </div>
          <div className="cfp">
            <span>Deal Discount</span>
            <b>${total.dealDiscountAmount}</b>
          </div>
          <div className="cfp">
            <span>Coupon Discount</span>
            <b>{couponData ? `${couponData.discount_per}%` : "No Discount"}</b>
          </div>
          <div className="cfp">
            <span>Subtotal (items)</span>
            <b>${itemsSubtotalDisplay}</b>
          </div>
          <div className="cfp">
            <span>Platform fee</span>
            <b>
              {taxLoading ? "…" : platformFeeDisplay != null ? `$${platformFeeDisplay}` : "—"}
            </b>
          </div>
          <div className="cfp">
            <span>{taxLabel}</span>
            <b>
              {taxLoading ? "…" : taxAmountDisplay != null ? `$${taxAmountDisplay}` : "—"}
            </b>
          </div>
          {taxPreviewError ? (
            <div className="cfp text-muted small">
              <span>Note</span>
              <b className="text-muted fw-normal">{taxPreviewError}</b>
            </div>
          ) : null}
          <div className="cfp">
            <span>Delivery Charges</span>
            <b>Free</b>
          </div>
          <div className="cfp">
            <span>Final Amount</span>
            <b>
              ${finalAmount}
              {taxIncludedInFinal ? <small> (incl. tax)</small> : null}
            </b>
          </div>
        </div>
      </div>
      <div className="cart_footer cart_wrap border-bottom justify-content-end">
        <div className="cart_foot_price">
          <div className="cfp">
            <span>Sub Total</span>
            <b>${finalAmount}</b>
          </div>
          {authInfo ? (
            <div className="cfp mt-4 text-center">
              {userDataAvailable === true ? ''
                :
                <div className="alert alert-danger" role="alert">
                  Profile update required! Complete your details to proceed with your order.
                </div>}
              <div className="d-grid col-6 mx-auto">
                {userDataAvailable === true ? (
                  <>
                     <button
                      className="btn custom_btn btn_yellow m-2"
                      onClick={openmodalHandler}
                      disabled={isLoading} 
                    >
                      {isLoading ? "Placing Order..." : "Place Order"}
                    </button>
                    {shouldShowMilestoneButton() && (
                      <>
                        To Pay in Milestones:
                        <button
                          type="button"
                          className="btn custom_btn btn_yellow m-2"
                          onClick={handleMilestoneClick}
                        >
                          Create Milestone Order
                        </button>
                      </>
                    )}
                  </> 
                ) : (
                  <Link className="btn custom_btn btn_yellow m-2" to="/my-profile">Complete Profile</Link>
                )}
              </div>
              <br /> OR
              <div className="ctn_btn m-4">
                <Link to="/" className="view_more">
                  Continue shopping
                </Link>
              </div>
            </div>
          ) : (
            <div className="cfp mt-4 text-center">
              <div className="alert alert-danger" role="alert">
                Login required! Login to proceed with your order.
              </div>
              <br /> OR
              <div className="ctn_btn m-4">
                <Link to="/" className="view_more">
                  Continue shopping
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Place Order Modal */}
      {showModal && (
        <PlaceOrderModal show={showModal} handleClose={handleCloseModal} />
      )}
      
      {console.log("SELLER ID", sellerId)}
      
      {/* Product Milestone Modal - Only show for multiple products/quantities */}
      {showMilestoneModal && shouldShowMilestoneButton() && cart && cart.length > 0 && (
        <ProductMilestoneModal
          show={showMilestoneModal}
          onHide={handleCloseMilestoneModal}
          productId={cart[0]?.id}
          productName={cart[0]?.name || "Product Order"}
          description="Product milestone-based order"
          charges={finalAmount}
          sellerId={sellerId}
          cart={cart}
          totalQuantity={total.totalQuantity}
          additionalData={additionalData}
        />
      )}
    </div>
  );
}

export default Total;